/**
 * Раскладка вкладки «Контент-план». Классы карточек, пилюль и плашек — глобальные из
 * base.css; здесь только сетка недель и строка слота, которой в base.css нет, потому что
 * она встречается ровно на этом экране.
 *
 * Слот — СТРОКА, а не плитка. Плитками он был, пока экран делили пополам с колонкой целей;
 * после её отключения (26.08) ширина освободилась, а у занятого слота появились два
 * действия — «перенести» и «отменить публикацию». В плитке шириной 232 px они переносятся
 * на вторую строку и ломают ритм сетки; в строке время, заголовок и действия стоят тремя
 * колонками и читаются одним взглядом сверху вниз.
 */

.page {
  /* Дашборд не растягивается во всю ширину монитора: строка таблицы на 1900px заставляет
     глаз ехать от края до края, а связь «подпись — значение» теряется. Предел один на три
     экрана, поэтому живёт токеном в tokens.css, а не тремя числами врозь. Экран черновика
     этот предел НЕ делит — у него свой (`--workspace-max`) и своё обоснование.
     Поля тоже токенами: на телефоне 32px с каждой стороны съедают шестую часть экрана. */
  width: 100%;
  max-width: var(--surface-max);
  margin-inline: auto;
  flex: 1;
  min-width: 0;
  padding: var(--page-pad-y) var(--page-pad-x);
  display: flex;
  flex-direction: column;
  gap: 18px;
  overflow-y: auto;
}

.head {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}

.headText {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Левая колонка целей сжимается до 320 px и не ниже: в ней строка «код формата + степпер»,
   и при меньшей ширине степпер уезжает под код. Правая — minmax(0, 1fr), иначе календарь
   с длинным заголовком поста растягивает страницу вместо того, чтобы обрезать заголовок. */
.cols {
  display: grid;
  grid-template-columns: minmax(320px, 420px) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

/* Та же сетка без колонки целей: одна колонка на всю ширину, minmax(0,·) сохраняется —
   он и держит длинный заголовок внутри страницы. */
.single {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}

.weeks {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Левая колонка — цели по типам публикаций: карточка на период. */
.targets {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.targetRow {
  gap: 12px;
  padding: 8px 0;
}

/* Имя формата (`formatLabel`), код — в подсказке. Ширина фиксирована, чтобы
   степперы справа стояли в одну линию, а не по длине имени формата. */
.formatCode {
  width: 132px;
  flex: none;
  font-size: 12.5px;
  color: var(--ink);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Степпер: минус, число, плюс — одной пилюлей, чтобы цель читалась как одно значение. */
.stepper {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border);
  border-radius: var(--r-pill);
  padding: 2px 6px;
  flex: none;
}
.stepper > span {
  min-width: 28px;
  text-align: center;
  font-size: 12.5px;
}
.stepper .edbtn {
  width: 20px;
  height: 20px;
}

.addRow {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.saveRow {
  display: flex;
  align-items: center;
  gap: 10px;
  border-top: 1px solid var(--border-2);
  padding-top: 12px;
  margin-top: 2px;
}

.weekHead {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* ══ строки слотов ══ */

/* Неделя — список строк расписания (ul/li): скринридер объявляет число слотов и позицию в
   нём, а маркеры и отступы браузера снимаются здесь. */
.slots {
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

/* Свободный слот — пунктир и приглушённый фон: пустое место должно читаться как пустое. */
.slotRow {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-style: dashed;
  border-radius: var(--r-ctl);
  background: var(--bg-alt);
  transition:
    border-color 200ms var(--ease),
    background 200ms var(--ease);
}

/* Занятый — сплошная рамка и белый фон. */
.slotTaken {
  border-style: solid;
  background: var(--white);
}

/* Время слота — колонка фиксированной ширины: так минуты стоят друг под другом и неделя
   читается как расписание, а не как список фраз. */
.slotTime {
  flex: none;
  width: 140px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--sans);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.slotTimeFree {
  font-weight: 400;
  color: var(--muted);
}

/* min-width: 0 обязателен: без него длинный заголовок не обрезается, а растягивает строку
   вместе со страницей. */
.slotBody {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.slotTitle {
  font-weight: 600;
  color: var(--ink);
  text-decoration: none;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
a.slotTitle:hover {
  color: var(--accent-ink);
}

.slotActions {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Ручка перетаскивания — весь занятый слот. Курсор говорит об этом раньше любой подписи. */
.slotDraggable {
  cursor: grab;
}
.slotDraggable:active {
  cursor: grabbing;
}

/* Слот, который сейчас переносят: он остаётся на месте до ответа сервера, поэтому помечен
   заливкой, а не исчезновением — исчезнувшая строка при отказе вернулась бы рывком. */
.slotSource {
  border-color: var(--violet);
  background: var(--violet-bg);
}

/* Куда упадёт. Подсветка появляется на том слоте, над которым курсор или фокус, — иначе
   «перетащите куда-нибудь» ничем не отличается от «перетащите вот сюда». */
.slotTarget {
  border-style: solid;
  border-color: var(--violet);
  background: var(--violet-bg);
}

/* Все слоты подсвечены разом — это и есть режим настройки сетки: человек видит границы
   всех строк, включая свободные, и понимает, что сейчас правится расписание. */
.slotEditing {
  border-color: var(--violet-bg-2);
}

.moveBar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border: 1px solid var(--violet-bg-2);
  border-radius: var(--r-ctl);
  background: var(--violet-bg);
}

.setupRow {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Поле времени: гарнитуру и размер задаём явно — системный контрол их не наследует. */
.slotInput {
  font-family: var(--sans);
  font-size: 12.5px;
  border: 1px solid var(--border);
  border-radius: var(--r-ctl);
  background: var(--white);
  color: var(--ink);
  padding: 7px 10px;
  max-width: 100%;
}
.slotInput:focus {
  outline: none;
  border-color: var(--violet-bg-2);
}

@media (max-width: 1280px) {
  .cols {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* Ниже 900 px три колонки строки в ряд не помещаются: вместо горизонтального скролла
   строка переносится, а колонка времени перестаёт держать фиксированную ширину. */
@media (max-width: 900px) {
  .slotRow {
    flex-wrap: wrap;
  }
  .slotTime {
    width: auto;
  }
  .slotBody {
    flex-basis: 100%;
  }
}

/*
 * Телефон (640): код формата отпускает свои 132px. Ширина фиксирована ради того, чтобы
 * степперы справа стояли в одну линию; на 390px эта же фиксация оставляла степперу 100px и
 * загоняла его под строку — то есть ровно ломала то, ради чего фиксировалась. В одну линию
 * на телефоне строка не встаёт ни при какой ширине колонки, поэтому ограничение снимается,
 * а не уменьшается.
 */
@media (max-width: 640px) {
  .formatCode {
    width: auto;
    min-width: 0;
  }
}
